﻿import { translate } from "app/filters/i18nFilter";
import { UNKNOWN_COUNTRY_ID, WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

declare const window: any;

type CountryAttributeDependencies = {
    allowedCountriesIds: Set<number>;
    mobileWebAllowedCountriesIds: Set<number>;
};

export interface ICountryObject<T = number> {
    code: string;
    id: T;
    icon?: string;
    text: string;
    states?: ICountryObject[];
    children?: ICountryObject[];
    parent?: any;
    mobileWeb?: boolean;
    permitted?: boolean;
    showDeviceIcon?: boolean;
    data?: number;
    key?: number;
    isLastPermitted?: boolean;
    tooltipText?: string;
    isClickAllowed?: boolean;
}

export interface ICountryService {
    countries: ICountryObject[];
    countriesById: { [key: number]: ICountryObject };
    isUSState: (code: number | string) => boolean;
    getCountryById: (id: number | string) => ICountryObject;
    getCountryByCode: (code: string) => ICountryObject | undefined;
    getGoogleCountryCodeById: (id: number | string) => string;
    isUnknownCountry(id: number): boolean;
    isWorldwideCountry(id: number): boolean;
    isCountrySupported(countryId: number): boolean;
    enrichCountryAttributes(
        deps: CountryAttributeDependencies,
    ): (country: Readonly<ICountryObject>) => Readonly<ICountryObject>;
    getCountriesByCodes(codes: string[]): ICountryObject[];
    getCountriesByIds(ids: readonly number[]): ICountryObject[];
}

class CountryService implements ICountryService {
    public countries: any[];

    public countriesById: { [key: number | string]: ICountryObject };

    constructor(
        countries: any[] = window.similarweb.config.countries ?? [],
        countriesById = window.similarweb.config.countriesById ?? {},
    ) {
        this.countries = countries;
        this.countriesById = countriesById;

        if (this.countries) {
            if (!this.countries.some(({ id }) => id === WORLDWIDE_COUNTRY_ID)) {
                this.countries.unshift({ id: WORLDWIDE_COUNTRY_ID, code: "ww" });
            }
            if (!this.countries.some(({ id }) => id === UNKNOWN_COUNTRY_ID)) {
                this.countries.unshift({ id: 0, code: "xx" });
            }
            this.countries.forEach((country) => {
                country.children = [];
                country.icon = "flag flag-" + country.code;
                country.text = this.isUnknownCountry(country.id)
                    ? translate("global.unknown")
                    : translate("common.country." + country.id);
                this.countriesById[country.id] = country;
                if (country.states && country.states.length) {
                    country.states.forEach((state: any) => {
                        state.text = translate("common.country." + state.id);
                        country.children.push(state);
                        this.countriesById[state.id] = state;
                    });
                }
            });
        }
    }

    public isUnknownCountry(countryId: number): boolean {
        return countryId === UNKNOWN_COUNTRY_ID;
    }

    public isWorldwideCountry(countryId: number): boolean {
        return countryId === WORLDWIDE_COUNTRY_ID;
    }

    public getCountryById(id: number | string): ICountryObject {
        return this.countriesById[id];
    }

    public getCountryByCode(code: string): ICountryObject | undefined {
        const countryKey = Object.keys(this.countriesById).find((country) => {
            return this.countriesById[country].code === code || this.countriesById[country].code === code.toLowerCase();
        });
        return countryKey ? this.countriesById[countryKey] : undefined;
    }

    public getGoogleCountryCodeById(id: any) {
        // the Google code for the worldwide is '', the rest of the codes are the upper case of the country code
        return Number(id) === WORLDWIDE_COUNTRY_ID ? String() : this.getCountryById(id)?.code?.toUpperCase();
    }

    public isUSState = (id: string | number) => {
        return this.countriesById[id] ? !!this.countriesById[id].parent : false;
    };

    public isCountrySupported = (countryId: number): boolean => {
        return typeof this.getCountryById(countryId) !== "undefined";
    };

    public enrichCountryAttributes = ({
        allowedCountriesIds,
        mobileWebAllowedCountriesIds,
    }: CountryAttributeDependencies) => {
        return (country: Readonly<ICountryObject>): Readonly<ICountryObject> => {
            const isWorldwideCountry = this.isWorldwideCountry(country.id);
            const isAllowedCountry = allowedCountriesIds.has(country.id);
            const isMobileWeb = mobileWebAllowedCountriesIds.has(country.id);

            return {
                ...country,
                permitted: isAllowedCountry,
                mobileWeb: isMobileWeb,
                showDeviceIcon: !isWorldwideCountry,
            };
        };
    };

    public getCountriesByCodes = (codes: string[]): ICountryObject[] => {
        const upperCaseCodes = codes.map((code) => code.toUpperCase());
        return this.countries.filter((country) => country.code && upperCaseCodes.includes(country.code.toUpperCase()));
    };

    public getCountriesByIds = (ids: readonly number[]): ICountryObject[] => {
        return this.countries.filter((country) => country.id && ids.includes(country.id));
    };
    // Helper method to initialize from window object if needed, but allows clean initialization
    // This preserves backward compatibility while enabling safe static imports
    public static getInstance(): ICountryService {
        if (!CountryService.instance) {
            const config = (typeof window !== "undefined" && window.similarweb && window.similarweb.config) || {};
            CountryService.instance = new CountryService(config.countries || [], config.countriesById || {});
        }
        return CountryService.instance;
    }

    private static instance: CountryService;
}

export const CountryServiceFactory = (countries: any[], countriesById = {}) => {
    return new CountryService(countries, countriesById);
};

const getCountryServicePropertyDescriptor = (
    instance: ICountryService,
    prop: string | symbol,
): PropertyDescriptor | undefined => {
    let current: object | null = instance;

    while (current && current !== Object.prototype) {
        const descriptor = Reflect.getOwnPropertyDescriptor(current, prop);
        if (descriptor) {
            return descriptor;
        }
        current = Reflect.getPrototypeOf(current);
    }

    return undefined;
};

// Export a proxy that lazy-initializes the service when accessed.
// Descriptor traps keep test spies and runtime overrides working like a normal object.
const countryServiceTarget = {} as ICountryService;
const countryServiceProxy = new Proxy(countryServiceTarget, {
    get: (target, prop, receiver) => {
        if (Reflect.has(target, prop)) {
            return Reflect.get(target, prop, receiver);
        }
        const instance = CountryService.getInstance();
        const value = Reflect.get(instance as object, prop, receiver);

        return typeof value === "function" ? value.bind(instance) : value;
    },
    getOwnPropertyDescriptor: (target, prop) => {
        const targetDescriptor = Reflect.getOwnPropertyDescriptor(target, prop);
        if (targetDescriptor) {
            return targetDescriptor;
        }

        const instance = CountryService.getInstance();
        const descriptor = getCountryServicePropertyDescriptor(instance, prop);
        if (!descriptor) {
            return undefined;
        }

        if ("value" in descriptor) {
            const { value } = descriptor;
            return {
                configurable: true,
                enumerable: descriptor.enumerable,
                value: typeof value === "function" ? value.bind(instance) : value,
                writable: true,
            };
        }

        return {
            configurable: true,
            enumerable: descriptor.enumerable,
            get: descriptor.get ? () => descriptor.get?.call(instance) : undefined,
            set: descriptor.set ? (value) => descriptor.set?.call(instance, value) : undefined,
        };
    },
    has: (target, prop) => {
        if (Reflect.has(target, prop)) {
            return true;
        }

        return prop in CountryService.getInstance();
    },
});

export default countryServiceProxy;
